import { Command } from 'cmdk';
import { Search, MapPin, Globe, Flag } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import type { CountryScore } from '@/services/scoreService';
import { getStatus, getStatusColor } from '@/data/regulatoryData';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  jurisdictions: CountryScore[];
  onSelect: (jurisdiction: CountryScore) => void;
}

const CommandPalette = ({ isOpen, onClose, jurisdictions, onSelect }: CommandPaletteProps) => {
  const [search, setSearch] = useState('');

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Reset search when opening
  useEffect(() => {
    if (isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  // Group jurisdictions by type
  const groupedJurisdictions = useMemo(() => {
    const countries: CountryScore[] = [];
    const usStates: CountryScore[] = [];
    const caProvinces: CountryScore[] = [];

    jurisdictions.forEach((j) => {
      if (j.code.startsWith('US-')) {
        usStates.push(j);
      } else if (j.code.startsWith('CA-')) {
        caProvinces.push(j);
      } else {
        countries.push(j);
      }
    });

    return { countries, usStates, caProvinces };
  }, [jurisdictions]);

  const getJurisdictionIcon = (code: string) => {
    if (code.startsWith('US-')) return Flag;
    if (code.startsWith('CA-')) return MapPin;
    return Globe;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Command Palette */}
      <Command
        className="relative glass-strong rounded-xl shadow-card-elevated max-w-lg w-full mx-4 overflow-hidden animate-fade-in"
        loop
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search className="h-4 w-4 text-text-tertiary" />
          <Command.Input
            value={search}
            onValueChange={setSearch}
            placeholder="Search jurisdictions..."
            className="flex-1 bg-transparent border-0 outline-none text-sm text-text-primary placeholder:text-text-tertiary"
            autoFocus
          />
          <kbd className="px-1.5 py-0.5 rounded bg-white/5 text-text-tertiary text-xs">
            esc
          </kbd>
        </div>

        {/* Results */}
        <Command.List className="max-h-80 overflow-y-auto p-2">
          <Command.Empty className="py-6 text-center text-sm text-text-secondary">
            No jurisdictions found.
          </Command.Empty>

          {/* Countries */}
          {groupedJurisdictions.countries.length > 0 && (
            <Command.Group heading="Countries" className="mb-2">
              <p className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-text-tertiary font-medium">
                Countries
              </p>
              {groupedJurisdictions.countries.map((jurisdiction) => (
                <JurisdictionItem
                  key={jurisdiction.code}
                  jurisdiction={jurisdiction}
                  icon={getJurisdictionIcon(jurisdiction.code)}
                  onSelect={() => {
                    onSelect(jurisdiction);
                    onClose();
                  }}
                />
              ))}
            </Command.Group>
          )}

          {/* US States */}
          {groupedJurisdictions.usStates.length > 0 && (
            <Command.Group heading="US States" className="mb-2">
              <p className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-text-tertiary font-medium">
                US States
              </p>
              {groupedJurisdictions.usStates.map((jurisdiction) => (
                <JurisdictionItem
                  key={jurisdiction.code}
                  jurisdiction={jurisdiction}
                  icon={getJurisdictionIcon(jurisdiction.code)}
                  onSelect={() => {
                    onSelect(jurisdiction);
                    onClose();
                  }}
                />
              ))}
            </Command.Group>
          )}

          {/* Canadian Provinces */}
          {groupedJurisdictions.caProvinces.length > 0 && (
            <Command.Group heading="Canadian Provinces" className="mb-2">
              <p className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-text-tertiary font-medium">
                Canadian Provinces
              </p>
              {groupedJurisdictions.caProvinces.map((jurisdiction) => (
                <JurisdictionItem
                  key={jurisdiction.code}
                  jurisdiction={jurisdiction}
                  icon={getJurisdictionIcon(jurisdiction.code)}
                  onSelect={() => {
                    onSelect(jurisdiction);
                    onClose();
                  }}
                />
              ))}
            </Command.Group>
          )}
        </Command.List>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-white/10 bg-white/5 flex items-center justify-between text-[10px] text-text-tertiary">
          <span>
            <kbd className="px-1 py-0.5 bg-white/10 rounded mr-1">↑↓</kbd>
            Navigate
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-white/10 rounded mr-1">Enter</kbd>
            Select
          </span>
        </div>
      </Command>
    </div>
  );
};

// Individual jurisdiction item
interface JurisdictionItemProps {
  jurisdiction: CountryScore;
  icon: React.ComponentType<{ className?: string }>;
  onSelect: () => void;
}

const JurisdictionItem = ({ jurisdiction, icon: Icon, onSelect }: JurisdictionItemProps) => {
  const status = getStatus(jurisdiction.score);
  const statusColor = getStatusColor(status);

  return (
    <Command.Item
      value={`${jurisdiction.name} ${jurisdiction.code}`}
      onSelect={onSelect}
      className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer
                 data-[selected=true]:bg-white/10 transition-colors"
    >
      <Icon className="h-4 w-4 text-text-tertiary" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-text-primary truncate">{jurisdiction.name}</p>
        <p className="text-xs text-text-tertiary">{jurisdiction.code}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-text-primary">{jurisdiction.score}</span>
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: statusColor }}
        />
      </div>
    </Command.Item>
  );
};

export default CommandPalette;
